// CO - Desconto progressivo — comportamento do bloco conso-desconto-progressivo.liquid.
//
// Fork do conso-oferta-quantidade.js com duas diferenças:
//
// 1. A quantidade vem do DEGRAU escolhido (rádio em cards), não de um setting.
//    O painel (selects + botão) existe uma vez e é MOVIDO para o card clicado,
//    que abre com animação. Os selects são renderizados no tamanho do maior
//    degrau; um degrau menor esconde os que sobram. A escolha de cada peça
//    sobrevive à troca — são os mesmos <select>.
//
// 2. O evento de carrinho é disparado no formato da Horizon 4
//    (`shopify:cart:lines-update`) DIRETO, com `action: 'add'` — e não o legado
//    `cart:update`. A ponte conso-cart-event-bridge re-emite o legado como
//    `'update'` de propósito (é para script de fundo, que não pode abrir a sacola),
//    e o cart-drawer.js da 4.x só auto-abre em `'add'`. Isto aqui é um clique do
//    cliente: a sacola DEVE abrir. Mesmo contrato do product-form.js — o handler
//    lê `event.action` e `event.promise`, e o promise resolve com
//    `{ cart, detail: { sections, itemCount, didError } }`.
//
// Uma chamada `/cart/add.js` com `items: [...]`, e não uma por unidade: a
// function `desconto-progressivo` soma a quantidade do produto no carrinho, e
// com uma requisição só a resposta já vem com o carrinho final — o drawer não
// pinta um estado intermediário sem o degrau.

if (!window.__consoDescontoProgressivoInit) {
  window.__consoDescontoProgressivoInit = true;

  const SOURCE_ID = 'conso-desconto-progressivo';
  const EVENTO_V4 = 'shopify:cart:lines-update';
  const ERRO_GENERICO = 'Não foi possível adicionar ao carrinho. Tente novamente.';

  const cartAddUrl = () => window.Theme?.routes?.cart_add_url || '/cart/add.js';
  const cartUrl = () => `${window.Theme?.routes?.cart_url || '/cart'}.js`;

  const getCartSectionIds = () => {
    return Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((component) => component.getAttribute('data-section-id'))
      .filter(Boolean)
      .filter((sectionId, index, all) => all.indexOf(sectionId) === index);
  };

  // Espelha o `animateAddToCart` do product-form.js: um teto fixo dá o mesmo
  // efeito sem depender do `animationend` — se a animação não rodar (reduced
  // motion), o botão volta ao normal do mesmo jeito.
  const ADDED_RESET_MS = 1000;

  class ConsoDescontoProgressivo extends HTMLElement {
    #inFlight = false;
    #resetTimer = null;

    connectedCallback() {
      if (this.__consoBound) return;
      this.__consoBound = true;

      this.addEventListener('change', this.#handleChange);
      this.addEventListener('click', this.#handleClick);

      // Sincroniza com o rádio marcado no HTML: o Liquid já imprime o painel no
      // card padrão e esconde os slots acima dele, mas o navegador pode ter
      // restaurado outro rádio (bfcache, autofill) — aí o painel precisa mudar
      // de card e o botão de texto.
      this.#abrir(this.#cardMarcado, false);
      this.#applyTier(this.#quantity);
    }

    get #painel() {
      return this.querySelector('[data-co-dp-painel]');
    }

    /** O card cujo rádio está marcado, ou null. */
    get #cardMarcado() {
      return this.querySelector('[data-co-dp-tier]:checked')?.closest('[data-co-dp-card]') ?? null;
    }

    // Move o painel (selects + botão) para dentro do card escolhido e abre o
    // card com animação. Mover o nó, e não clonar, é o que faz a escolha de cada
    // peça sobreviver à troca de degrau — são os mesmos <select>.
    //
    // A classe entra num frame depois do appendChild: no mesmo frame o grid
    // sairia de 0fr para 1fr sem transição, porque o navegador ainda não pintou
    // o estado fechado com o conteúdo dentro.
    #abrir(card, animar = true) {
      const painel = this.#painel;
      if (!card || !painel) return;

      const atual = this.querySelector('.co-dp__card--aberto');
      if (atual && atual !== card) atual.classList.remove('co-dp__card--aberto');

      const alvo = card.querySelector('[data-co-dp-corpo]');
      if (alvo && painel.parentElement !== alvo) alvo.appendChild(painel);

      if (!animar) {
        card.classList.add('co-dp__card--aberto');
        return;
      }

      requestAnimationFrame(() => card.classList.add('co-dp__card--aberto'));
    }

    get #selects() {
      return Array.from(this.querySelectorAll('[data-co-dp-select]'));
    }

    get #slots() {
      return Array.from(this.querySelectorAll('[data-co-dp-slot]'));
    }

    /** Quantidade do degrau marcado. 0 quando nenhum (só linhas informativas). */
    get #quantity() {
      const checked = this.querySelector('[data-co-dp-tier]:checked');
      const value = Number(checked?.value ?? this.dataset.qtdPadrao ?? 0);
      return Number.isFinite(value) && value > 0 ? value : 0;
    }

    #handleChange = (event) => {
      if (event.target.closest('[data-co-dp-tier]')) {
        this.#abrir(this.#cardMarcado);
        this.#applyTier(this.#quantity);
        return;
      }

      const select = event.target.closest('[data-co-dp-select]');
      if (!select) return;

      const thumb = select.closest('[data-co-dp-slot]')?.querySelector('[data-co-dp-thumb]');
      const src = select.selectedOptions[0]?.dataset.thumb;
      if (thumb && src) thumb.src = src;
    };

    #handleClick = (event) => {
      if (!event.target.closest('[data-co-dp-add]')) return;
      event.preventDefault();
      this.#add();
    };

    // Mostra os N primeiros slots, esconde o resto, e escreve o N no botão.
    #applyTier(quantity) {
      this.#slots.forEach((slot, index) => {
        slot.hidden = index >= quantity;
      });

      const texto = this.querySelector('[data-co-dp-add-texto]');
      const modelo = this.dataset.textoBotao || 'Adicionar {n} ao carrinho';
      if (texto) texto.textContent = modelo.replace('{n}', String(quantity));

      const button = this.querySelector('[data-co-dp-add]');
      if (button && !this.#inFlight) button.disabled = quantity === 0;
    }

    #setLoading(loading) {
      const button = this.querySelector('[data-co-dp-add]');
      if (!button) return;
      button.disabled = loading;
      button.setAttribute('aria-busy', loading ? 'true' : 'false');
      this.classList.toggle('co-dp--loading', loading);
    }

    // As duas animações do CTA do tema: o burst de check no botão e a miniatura
    // voando até o ícone do carrinho. Moram no product-form.js, que este bloco
    // não usa, então são reproduzidas aqui.
    #animate() {
      if (this.dataset.animarAdd === 'false') return;

      const button = this.querySelector('[data-co-dp-add]');
      if (!button) return;

      button.dataset.added = 'true';
      clearTimeout(this.#resetTimer);
      this.#resetTimer = setTimeout(() => button.removeAttribute('data-added'), ADDED_RESET_MS);

      const cartIcon = document.querySelector('.header-actions__cart-icon');
      const image = this.#selects[0]?.selectedOptions[0]?.dataset.thumb || this.dataset.thumbProduto;
      if (!cartIcon || !image || !customElements.get('fly-to-cart')) return;

      const flyToCart = document.createElement('fly-to-cart');
      flyToCart.classList.add('fly-to-cart--main');
      flyToCart.style.setProperty('background-image', `url(${image})`);
      flyToCart.style.setProperty('--start-opacity', '0');
      flyToCart.source = button;
      flyToCart.destination = cartIcon;
      document.body.appendChild(flyToCart);
    }

    #setError(message) {
      const box = this.querySelector('[data-co-dp-error]');
      if (!box) return;
      box.textContent = message || '';
      box.hidden = !message;
    }

    // Uma linha por variante distinta: a mesma variante em dois selects vira
    // quantity 2, e não duas linhas iguais na sacola. Produto sem variantes
    // (sem selects) manda N da única.
    #buildItems(quantity) {
      const single = Number(this.dataset.variantId);
      if (single && this.#selects.length === 0) {
        return [{ id: single, quantity }];
      }

      const items = [];

      for (const select of this.#selects.slice(0, quantity)) {
        const id = Number(select.value);
        if (!id) continue;

        const existing = items.find((item) => item.id === id);
        if (existing) existing.quantity += 1;
        else items.push({ id, quantity: 1 });
      }

      return items;
    }

    async #add() {
      if (this.#inFlight) return;

      const quantity = this.#quantity;
      const items = this.#buildItems(quantity);
      if (quantity === 0 || items.length === 0) return;

      this.#inFlight = true;
      this.#setLoading(true);
      this.#setError('');

      try {
        const body = { items };
        const sectionIds = getCartSectionIds();
        if (sectionIds.length > 0) {
          body.sections = sectionIds.join(',');
          body.sections_url = window.location.pathname;
        }

        const response = await fetch(cartAddUrl(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });

        const payload = await response.json();

        // `/cart/add.js` devolve 422 com `description` quando uma variante ficou
        // sem estoque entre o render da página e o clique.
        if (!response.ok || payload.status) {
          throw new Error(payload.description || payload.message || ERRO_GENERICO);
        }

        // Antes do GET /cart.js: a animação é feedback do clique e não deve
        // esperar um round-trip a mais para começar.
        this.#animate();

        const sections = payload.sections && typeof payload.sections === 'object' ? payload.sections : undefined;

        const cart = await fetch(cartUrl(), { headers: { Accept: 'application/json' } })
          .then((res) => (res.ok ? res.json() : null))
          .catch(() => null);

        const evento = new CustomEvent(EVENTO_V4, { bubbles: true });
        // Lidos direto do evento pelo cart-drawer.js e component-cart-items.js,
        // não do detail. `'add'` é o que abre a sacola.
        evento.action = 'add';
        evento.promise = Promise.resolve({
          cart,
          detail: {
            sections,
            itemCount: Number(cart?.item_count ?? 0),
            source: SOURCE_ID,
            didError: false,
          },
        });
        document.dispatchEvent(evento);
      } catch (error) {
        this.#setError(error?.message || ERRO_GENERICO);
      } finally {
        this.#inFlight = false;
        this.#setLoading(false);
        this.#applyTier(this.#quantity);
      }
    }
  }

  if (!customElements.get('conso-desconto-progressivo')) {
    customElements.define('conso-desconto-progressivo', ConsoDescontoProgressivo);
  }
}
